import { useState } from "react";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRespondToSwap } from "@/hooks/schedule";
import type { SwapOffer } from "@/hooks/schedule";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Clock, MapPin, MessageSquare, X } from "lucide-react";

interface SwapOfferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  swapOffer: SwapOffer | null;
  currentWeek?: Date;
}

export function SwapOfferDialog({ open, onOpenChange, swapOffer, currentWeek }: SwapOfferDialogProps) {
  const isMobile = useIsMobile();
  const [isResponding, setIsResponding] = useState(false);
  const respondToSwap = useRespondToSwap(currentWeek);

  if (!swapOffer) return null;

  const { 
    requester_name, 
    message, 
    requester_shift_start, 
    requester_shift_end,
    my_shift_start,
    my_shift_end,
    candidate_status 
  } = swapOffer;

  // Check if the offer is actionable (only pending offers can be responded to)
  const isActionable = candidate_status === 'pending';

  // Get status display information
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return { text: 'New Offer', color: 'text-pink-700', bgColor: 'bg-pink-50', borderColor: 'border-pink-300' };
      case 'accepted':
        return { text: 'Accepted', color: 'text-green-700', bgColor: 'bg-green-50', borderColor: 'border-green-300' };
      case 'declined':
        return { text: 'Declined', color: 'text-red-700', bgColor: 'bg-red-50', borderColor: 'border-red-300' };
      case 'expired':
        return { text: 'Expired', color: 'text-gray-700', bgColor: 'bg-gray-50', borderColor: 'border-gray-300' };
      default:
        return { text: 'New Offer', color: 'text-pink-700', bgColor: 'bg-pink-50', borderColor: 'border-pink-300' };
    }
  };

  const statusInfo = getStatusInfo(candidate_status);

  const formatShiftTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
  };

  const formatShiftDate = (startTime: string) => {
    return format(new Date(startTime), 'EEEE, MMMM d, yyyy');
  };

  const getInitials = (firstName?: string, lastName?: string, fullName?: string) => {
    if (fullName) {
      return fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = (name: string) => {
    return name?.trim() || 'Unknown User';
  };

  const handleResponse = async (accept: boolean) => {
    if (isResponding) return;

    setIsResponding(true);
    try {
      await respondToSwap.mutateAsync({
        candidateId: swapOffer.candidate_id,
        response: accept ? 'accepted' : 'declined',
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error responding to swap:', error);
    } finally {
      setIsResponding(false);
    }
  };

  // Shared content component
  const content = (
    <div className="space-y-6">
      {/* Header with requester info */}
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-blue-100 text-blue-700">
            {getInitials(undefined, undefined, requester_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground">
            {getDisplayName(requester_name)} wants to swap shifts
          </p>
          <p className="text-sm text-muted-foreground">
            Swap request
          </p>
        </div>
        <Badge 
          variant="outline" 
          className={`${statusInfo.color} ${statusInfo.borderColor} ${statusInfo.bgColor}`}
        >
          {statusInfo.text}
        </Badge>
      </div>

      {/* Message if provided */}
      {message && (
        <Card className="bg-muted/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-sm text-foreground">{message}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shift comparison */}
      <div className="space-y-4">
        {/* Their shift (what they're offering) */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">
            They want to give you:
          </h4>
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h5 className="font-medium text-foreground">
                    {formatShiftDate(requester_shift_start)}
                  </h5>
                  <Badge variant="outline" className="text-blue-700 border-blue-300">
                    Their Shift
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {formatShiftTime(requester_shift_start, requester_shift_end)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Your shift (what you're giving up) */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">
            In exchange for your shift:
          </h4>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h5 className="font-medium text-foreground">
                    {formatShiftDate(my_shift_start)}
                  </h5>
                  <Badge variant="outline" className="text-orange-700 border-orange-300">
                    Your Shift
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {formatShiftTime(my_shift_start, my_shift_end)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

  // Shared button group - only show action buttons for pending offers
  const buttonGroup = isActionable ? (
    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2">
      <Button 
        variant="outline" 
        onClick={() => handleResponse(false)}
        disabled={isResponding}
        className="text-red-600 border-red-200 hover:bg-red-50"
      >
        {isResponding ? "Declining..." : "Decline Swap"}
      </Button>
      <Button 
        onClick={() => handleResponse(true)}
        disabled={isResponding}
        className="bg-blue-600 hover:bg-blue-700"
      >
        {isResponding ? "Accepting..." : "Accept Swap"}
      </Button>
    </div>
  ) : (
    <div className="flex justify-end">
      <Button 
        variant="outline" 
        onClick={() => onOpenChange(false)}
      >
        Close
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="z-[60]">
          <DrawerHeader className="text-left">
            <div className="flex items-center justify-between">
              <DrawerTitle>Swap Offer</DrawerTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </DrawerHeader>
          <div className="px-4 pb-4">
            {content}
          </div>
          <DrawerFooter className="px-4">
            {buttonGroup}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
        <DialogOverlay className="fixed inset-0 z-[60] bg-black/50" />
        <DialogContent className="z-[60] sm:max-w-[600px]" aria-describedby="swap-offer-description">
          <DialogHeader>
            <DialogTitle>Swap Offer</DialogTitle>
          </DialogHeader>
          <div id="swap-offer-description" className="sr-only">
            Dialog showing a shift swap offer with details of both shifts and options to accept or decline.
          </div>
          {content}
          <DialogFooter>
            {buttonGroup}
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
